import type { CreatePartner, UpdatePartner } from '@pulsify/shared/dto'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { partnersApi } from '~/entities/partner/api/partners'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

export function usePartners() {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_PARTNERS),
    queryFn: () => partnersApi.list(),
    enabled: !!session && storageManager.isAdmin(),
  })
}

export function usePartnerDetail(id: string) {
  const session = useSupabaseSession()
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_PARTNERS, id),
    queryFn: () => partnersApi.detail(id),
    enabled: !!session && storageManager.isAdmin(),
  })
}

function usePartnerMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  successMessage: () => string,
  logLabel: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.ADMIN_PARTNERS) })
      toast.success(successMessage())
    },
    onError: (error: unknown) => {
      log.error(error as Error, `[Partners] ${logLabel} failed`)
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useCreatePartner() {
  return usePartnerMutation(
    (input: CreatePartner) => partnersApi.create(input),
    m.partnersCreated,
    'Create',
  )
}

export function useUpdatePartner(id: string) {
  return usePartnerMutation(
    (input: UpdatePartner) => partnersApi.update(id, input),
    m.partnersSaved,
    'Update',
  )
}

export function useAssignBusinessPartner() {
  return usePartnerMutation(
    ({ businessId, partnerId }: { businessId: string; partnerId: string | null }) =>
      partnersApi.assignBusiness(businessId, partnerId),
    m.partnersAssignmentSaved,
    'Assign',
  )
}
